export const CrossAppEvents = {
  MobileCancel: 'mobile-cancel-event',
  MobileDataHandover: 'mobile-data-handover-event',
  MobileComplete: 'mobile-complete-event',
  MobileDownload: 'mobile-download-event',
} as const;

/**
 * BroadcastChannel name for cross-tab / cross-stack auth signalling between the
 * React (expedo-fe) and Angular (hr-fe) stacks. **Both stacks must use this exact
 * name and message shape** (expedo-fe EF-4 ↔ hr-fe HF-4).
 */
export const XEMPLO_AUTH_CHANNEL = 'xemplo-auth';

/** Message `type` values broadcast on {@link XEMPLO_AUTH_CHANNEL}. */
export const AuthBroadcastType = {
  Logout: 'logout',
  /**
   * A new identity signed in on this origin. Carries the new `sub`; a tab whose
   * locally-cached user has a different `sub` must re-establish auth. Covers
   * same-tab user switching (logout → log in as someone else → cross to the other
   * stack), which a logout-only signal misses.
   */
  Login: 'login',
} as const;

/**
 * localStorage key holding the epoch-ms timestamp of the last cross-app logout.
 *
 * The durable companion to {@link XEMPLO_AUTH_CHANNEL}: the broadcast only reaches
 * tabs open at logout time, whereas this marker lets an app loading later (same
 * tab, after navigating away and back) detect that a logout happened and discard a
 * token issued before it. **Both stacks must use this exact key.**
 */
export const XEMPLO_AUTH_LOGOUT_KEY = 'xemplo-auth:logoutAt';

/**
 * localStorage key holding the `sub` (subject claim) of the identity currently
 * authenticated on this origin.
 *
 * The identity-aware companion to {@link XEMPLO_AUTH_LOGOUT_KEY}: the logout marker
 * only records *that* a logout happened, so it can't distinguish "logged back in as
 * the same user" from "logged in as someone else". Both stacks write this on every
 * successful sign-in and remove it on logout; a stack loading later (same tab, after
 * navigating away and back) compares its cached user's `sub` against this value and
 * re-establishes auth when they differ. **Both stacks must use this exact key.**
 */
export const XEMPLO_AUTH_ACTIVE_SUB_KEY = 'xemplo-auth:activeSub';

/**
 * Framed app → host: take the top-level page to `payload.url`, since a frame cannot.
 *
 * **Both stacks must use this exact name and message shape.** hr-fe's `iframeEvents`
 * (`src/app/constants/events/iframe-events.js`) has no NAVIGATE entry yet, so nothing
 * honours this today — it needs one, plus an `on()` handler, before the frame can
 * move the host.
 */
export const HOST_FRAME_NAVIGATE = 'iframe:navigate';
